import { useCallback, useEffect } from 'react';
import './landing.css';
import { Navbar } from './Navbar';
import { SplashScreen } from './SplashScreen';
import { shouldShowSplash } from './splashUtils';
import { HeroSection } from './HeroSection';
import { SocialProofBar } from './SocialProofBar';
import { FeaturesSection } from './FeaturesSection';
import { FloorPlanSection } from './FloorPlanSection';
import { HowItWorksSection } from './HowItWorksSection';
import { PricingSection } from './PricingSection';
import { ROISection } from './ROISection';
import { TestimonialsSection } from './TestimonialsSection';
import { FAQSection } from './FAQSection';
import { FooterSection } from './FooterSection';
import { LoginModal } from './LoginModal';
import { useState } from 'react';

interface LandingPageProps {
  onForgotPassword?: () => void;
  initialShowLogin?: boolean;
  initialAuthTab?: 'signin' | 'signup';

  onBlogClick?: () => void;
  onDatenschutzClick?: () => void;
  onImpressumClick?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onForgotPassword, initialShowLogin = false, initialAuthTab, onBlogClick, onDatenschutzClick, onImpressumClick }) => {
  const [showLogin, setShowLogin] = useState(initialShowLogin);
  const [authTab, setAuthTab] = useState<'signin' | 'signup'>(initialAuthTab ?? 'signin');
  const [showSplash, setShowSplash] = useState(() => !initialShowLogin && shouldShowSplash());

  const handleCtaClick = useCallback(() => {
    setShowLogin(true);
    setAuthTab('signup');
    history.pushState(null, '', '/signup');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleLoginClick = useCallback(() => {
    setShowLogin(true);
    setAuthTab('signin');
    history.pushState(null, '', '/login');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleBackToLanding = useCallback(() => {
    setShowLogin(false);
    history.pushState(null, '', '/');
  }, []);

  // Handle browser back/forward between / and /login
  useEffect(() => {
    const handlePopState = () => {
      const p = window.location.pathname;
      if (p === '/login') { setShowLogin(true); setAuthTab('signin'); }
      else if (p === '/signup') { setShowLogin(true); setAuthTab('signup'); }
      else setShowLogin(false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <div className="landing-root">
      {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
      <Navbar onCtaClick={handleCtaClick} onLoginClick={handleLoginClick} onBlogClick={onBlogClick} />
      <HeroSection onCtaClick={handleCtaClick} onLoginClick={handleLoginClick} />
      <SocialProofBar />
      <FeaturesSection />
      <FloorPlanSection onCtaClick={handleCtaClick} />
      <HowItWorksSection onCtaClick={handleCtaClick} />
      <PricingSection onCtaClick={handleCtaClick} />
      <ROISection />
      <TestimonialsSection onCtaClick={handleCtaClick} />
      <FAQSection />
      <FooterSection onBlogClick={onBlogClick} onDatenschutzClick={onDatenschutzClick} onImpressumClick={onImpressumClick} />

      {/* Auth modal overlays the landing — landing stays mounted in the background */}
      <LoginModal
        open={showLogin}
        initialTab={authTab}
        onClose={handleBackToLanding}
        onForgotPassword={onForgotPassword}
      />
    </div>
  );
};
